/**
 * Country registry — frontend mirror of `/app/backend/countries.py`.
 *
 * Anywhere the UI needs country-specific behaviour (currency, energy
 * label, regulatory body, postcode label, county list, etc.) it MUST
 * read from here rather than hardcoding "€" or "BER" inline.
 *
 * Keep this in sync with the backend `COUNTRIES` dict — adding a new
 * field here needs the same field added there.
 */

export const COUNTRIES = {
  IE: {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪',
    currency: 'EUR',
    currencySymbol: '€',
    currencyLocale: 'en-IE',
    vatRate: 0.23,
    postcodeScheme: 'Eircode',
    postcodeLabel: 'Eircode',
    postcodeExample: 'D02 X285',
    energyLabel: 'BER',
    energyProvider: 'SEAI',
    regulator: {
      name: 'PSRA',
      fullName: 'Property Services Regulatory Authority',
      licenceLabel: 'PSRA licence',
      licenceField: 'psra_licence',
      verifyUrl: 'https://www.psr.ie/en/PSRA/Pages/Find_a_licensee',
    },
    competitorPortal: 'Daft.ie',
    competitorImporterRoute: '/migrate-from-daft',
    phoneCountryCode: '+353',
  },
  UK: {
    code: 'UK',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    currencyLocale: 'en-GB',
    vatRate: 0.20,
    postcodeScheme: 'UK postcode',
    postcodeLabel: 'Postcode',
    postcodeExample: 'SW1A 1AA',
    energyLabel: 'EPC',
    energyProvider: 'EPC Register',
    regulator: {
      name: 'Propertymark',
      fullName: 'Propertymark (ARLA · NAEA · ARMA)',
      licenceLabel: 'Propertymark number',
      licenceField: 'propertymark_number',
      verifyUrl: 'https://www.propertymark.co.uk/find-an-expert',
    },
    competitorPortal: 'your other portal',
    competitorImporterRoute: '/import-saved-searches',
    phoneCountryCode: '+44',
  },
};

export const DEFAULT_COUNTRY_CODE = 'IE';

export const getCountry = (codeOrName) => {
  if (!codeOrName) return COUNTRIES.IE;
  const c = String(codeOrName).trim();
  if (COUNTRIES[c.toUpperCase()]) return COUNTRIES[c.toUpperCase()];
  if (c === 'Ireland') return COUNTRIES.IE;
  if (c === 'United Kingdom' || c === 'Great Britain' || c === 'GB' || c === 'Britain') {
    return COUNTRIES.UK;
  }
  return COUNTRIES.IE;
};

/** Format a price in the country's local currency. */
export const formatPrice = (amount, countryCode = 'IE') => {
  if (amount == null || isNaN(amount)) return '';
  const c = getCountry(countryCode);
  try {
    return new Intl.NumberFormat(c.currencyLocale, {
      style: 'currency',
      currency: c.currency,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  } catch {
    return `${c.currencySymbol}${Math.round(amount).toLocaleString()}`;
  }
};

/**
 * Derive the country code for a property given its raw location object.
 * Property docs in the DB store `location.country` as the long-form name
 * ("Ireland" / "United Kingdom") so we map those to codes here.
 */
export const propertyCountryCode = (property) => {
  const ctry = property?.location?.country || property?.country;
  return getCountry(ctry).code;
};
